import { Icons } from '@/components/icons';
import TicketCuticle from '@/components/manage/TicketCuticle';
import LegDate from '@/components/manage/tickets/LegDate';
import ProductDetails from '@/components/manage/tickets/ProductDetails';
import TransferTime from '@/components/manage/tickets/TransferTime';
import TransportBadge from '@/components/manage/tickets/TransportBadge';
import TripDetails from '@/components/manage/tickets/TripDetails';
import { Badge } from '@/components/primitives/Badge';
import { BackgroundThemeProvider } from '@/context/BackgroundThemeContextProvider';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, LegDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import {
  areAllAdmissionsWithStatus,
  areRefundedLike,
  areSomeAdmissionsWithStatus,
} from '@/utils/admission';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { getDurationBetweenTimestamps } from '@/utils/dateTime';
import { type FC, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

interface LegCardProps {
  leg: LegDTO;
  nextLeg?: LegDTO;
  admissions: Array<AdmissionDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
}

const LegCard: FC<LegCardProps> = ({
  leg,
  nextLeg,
  admissions,
  passengersMap,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const allExchanged = areAllAdmissionsWithStatus(admissions, 'EXCHANGED');
  const refundedLike = areRefundedLike(admissions);

  const badgeType = useMemo(() => {
    const someRefunded = areSomeAdmissionsWithStatus(admissions, 'REFUNDED');
    const someReleased = areSomeAdmissionsWithStatus(admissions, 'RELEASED');
    const someFulfilled = areSomeAdmissionsWithStatus(admissions, 'FULFILLED');

    if (refundedLike) {
      return 'refunded';
    } else if (someRefunded && someFulfilled) {
      return 'partiallyRefunded';
    } else if (someReleased && !someRefunded) {
      return 'partiallyCancelled';
    }
    return null;
  }, [admissions, refundedLike]);

  const transferTime = nextLeg
    ? getDurationBetweenTimestamps(leg.arrivalTime, nextLeg.departureTime)
    : null;

  const renderMobile = () => {
    return (
      <div
        className={cn(
          'flex flex-col rounded-lg border border-neutral-light bg-white text-dark',
          { 'bg-neutral-pale text-neutral': refundedLike }
        )}
      >
        <div className="flex flex-row items-center justify-between p-3">
          <TransportBadge leg={leg} refundedLike={refundedLike} />
          <LegDate date={leg.departureTime} />
        </div>
        <div className="px-3 pb-3">
          <TripDetails leg={leg} />
        </div>
        <div className="relative py-3">
          <TicketCuticle isVertical={false} />
          {(refundedLike ||
            badgeType === 'partiallyRefunded' ||
            badgeType === 'partiallyCancelled') && (
            <div className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
              {refundedLike && (
                <Badge variant="disabled" className="h-1/4">
                  <Icons.ticketReturn height={16} width={16} />
                  <TransText i18nKey="refunded" />
                </Badge>
              )}
              {badgeType === 'partiallyRefunded' && (
                <Badge variant="attention" className="h-1/4">
                  <Icons.infoCircle height={16} width={16} />
                  <TransText i18nKey="partiallyRefunded" />
                </Badge>
              )}
              {badgeType === 'partiallyCancelled' && (
                <Badge variant="attention" className="h-1/4">
                  <Icons.infoCircle height={16} width={16} />
                  <TransText i18nKey="partiallyCancelled" />
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="px-3 pb-3">
          <ProductDetails
            leg={leg}
            admissions={admissions}
            passengersMap={passengersMap}
          />
        </div>
      </div>
    );
  };

  const renderLaptopOrBigger = () => {
    return (
      <div
        className={cn(
          'relative my-3 flex flex-row rounded-lg border border-neutral-light bg-white text-dark laptop:flex-row',
          { 'bg-neutral-pale text-neutral': refundedLike }
        )}
      >
        <div className="flex w-full flex-row justify-between">
          <div className="flex w-full flex-row gap-2 p-4">
            <TripDetails leg={leg} />
          </div>
          {refundedLike && (
            <Badge variant="disabled" className="mt-3 h-1/4">
              <Icons.ticketReturn height={16} width={16} />
              <TransText i18nKey="refunded" />
            </Badge>
          )}
          {badgeType === 'partiallyRefunded' && (
            <Badge variant="attention" className="mt-3 h-1/4">
              <Icons.infoCircle height={16} width={16} />
              <TransText i18nKey="partiallyRefunded" />
            </Badge>
          )}
          {badgeType === 'partiallyCancelled' && (
            <Badge variant="attention" className="mt-3 h-1/4">
              <Icons.infoCircle height={16} width={16} />
              <TransText i18nKey="partiallyCancelled" />
            </Badge>
          )}
        </div>

        <div className="pl-4">
          <TicketCuticle isVertical />
        </div>

        <div className="flex w-2/3 flex-col gap-4 p-3 pl-2">
          <div className="flex flex-row items-center justify-between pl-2">
            <TransportBadge leg={leg} refundedLike={refundedLike} />
            <LegDate date={leg.departureTime} />
          </div>
          <ProductDetails
            leg={leg}
            admissions={admissions}
            passengersMap={passengersMap}
          />
        </div>
      </div>
    );
  };

  if (allExchanged) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <BackgroundThemeProvider backgroundTheme="white">
        {isLaptopOrBigger ? renderLaptopOrBigger() : renderMobile()}
      </BackgroundThemeProvider>
      {Boolean(transferTime) && (
        <TransferTime
          formattedTime={transferTime}
          arrivalStation={leg.destinationStop?.name}
          departureStation={nextLeg?.originStop?.name}
        />
      )}
    </div>
  );
};

export default LegCard;
